import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  chatThemeSchema,
  DEFAULT_INTERFACE_THEME,
  supportedLanguages,
  VISITOR_NAME_AAD_CID,
  type ChatTheme,
  type LanguageCode,
} from '@kairelo/shared';
import { setVisitorToken, visitor as visitorApi } from '../lib/api.js';
import { clearVisitorSession, loadVisitorSession } from '../lib/visitorSession.js';
import { loadConversationKey } from '../lib/keystore.js';
import { decryptPayload } from '../lib/crypto.js';
import { InterfaceThemeProvider } from '../theme/InterfaceTheme.tsx';
import { I18nProvider } from '../i18n/I18n.tsx';
import { ChatPane } from '../chat/ChatPane.tsx';
import { ReportDialog } from '../chat/ReportDialog.tsx';
import { G } from '../chat/icons.tsx';
import { Modal, Field, Button } from '../components/ui.tsx';
import { LanguageSelect } from '../components/LanguageSelect.tsx';
import { loadVisitorLanguage, saveVisitorLanguage } from '../lib/visitorLanguage.js';
import { useT } from '../i18n/I18n.tsx';
import {
  EncryptionScopeNotice,
  EndpointCopyingNotice,
  NotAnonymousNotice,
  TemporaryNotice,
} from '../components/Disclosures.tsx';

/**
 * The visitor's conversation.
 *
 * A single pane: a visitor has exactly one conversation and no list. The
 * interface theme is the owner's, so both participants see the same product —
 * and the owner's branding tints it, as concept §10 requires.
 *
 * Reconnection works from the same browser during the active period. An
 * unrelated browser presenting no credential gets nothing, whoever holds the
 * original URL.
 */
export function VisitorConversation() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  // A visitor has no account to store a preference on, so the choice lives in
  // this browser: the browser's own language until they pick another from the
  // chat menu. It drives the interface and the translation target alike.
  const [language, setLanguage] = useState<LanguageCode>(loadVisitorLanguage);

  const [state, setState] = useState<'loading' | 'ready' | 'no-session' | 'ended'>('loading');
  const [token, setToken] = useState<string | null>(null);
  const [key, setKey] = useState<CryptoKey | null>(null);
  const [theme, setTheme] = useState<ChatTheme>(chatThemeSchema.parse({}));
  const [ownerName, setOwnerName] = useState('');
  const [dialog, setDialog] = useState<'none' | 'about' | 'report' | 'language'>('none');

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const session = loadVisitorSession(id);
      if (!session) {
        if (!cancelled) setState('no-session');
        return;
      }
      setVisitorToken(session.sessionToken);
      setToken(session.sessionToken);

      try {
        const [details, localKey] = await Promise.all([
          visitorApi.session(),
          loadConversationKey(id),
        ]);
        if (cancelled) return;

        setTheme(details.profile.theme);
        setOwnerName(details.profile.displayName);
        setKey(localKey);

        // The visitor's own name is conversation content too; decrypting it
        // here is only possible because this browser holds the key.
        if (localKey && details.visitorName) {
          try {
            await decryptPayload({
              key: localKey,
              conversationId: VISITOR_NAME_AAD_CID,
              sender: 'visitor',
              kind: 'visitor_name',
              envelope: details.visitorName,
            });
          } catch {
            // Not fatal: the conversation is still readable.
          }
        }
        setState('ready');
      } catch {
        if (!cancelled) {
          clearVisitorSession();
          setState('ended');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (state === 'loading') {
    return (
      <I18nProvider language={language}>
        <InterfaceThemeProvider themeId={DEFAULT_INTERFACE_THEME} className="msgr">
          <div className="msgr__frame" style={{ gridTemplateColumns: '1fr' }}>
            <div className="conv-empty">Reconnecting…</div>
          </div>
        </InterfaceThemeProvider>
      </I18nProvider>
    );
  }

  if (state === 'no-session' || state === 'ended') {
    return (
      <I18nProvider language={language}>
        <InterfaceThemeProvider themeId={DEFAULT_INTERFACE_THEME} className="msgr">
          <div className="msgr__frame" style={{ gridTemplateColumns: '1fr' }}>
            <div className="no-chat">
              <G n={state === 'ended' ? 'clock' : 'lock'} size={64} className="no-chat__art" />
              <h2>
                {state === 'ended'
                  ? 'This conversation has ended'
                  : 'This browser is not part of this conversation'}
              </h2>
              {state === 'ended' ? (
                <p>
                  The 24-hour period is over. There is no transcript, and the encrypted copy the
                  service held is queued for deletion.
                </p>
              ) : (
                <>
                  <p>
                    Joining creates a session credential stored in the browser that joined, and
                    nowhere else. This browser does not have one.
                  </p>
                  <p>
                    If you joined here and cleared your browsing data, the conversation cannot be
                    recovered — there is no account to fall back on.
                  </p>
                </>
              )}
              <button type="button" className="btn" onClick={() => navigate('/')}>
                Go to the start
              </button>
            </div>
          </div>
        </InterfaceThemeProvider>
      </I18nProvider>
    );
  }

  return (
    <I18nProvider language={language}>
      <InterfaceThemeProvider
        themeId={DEFAULT_INTERFACE_THEME}
        accentOverride={theme.accentColour}
        className="msgr msgr--chat"
      >
        <div className="msgr__frame" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
          <ChatPane
            conversationId={id}
            role="visitor"
            language={language}
            token={token ?? undefined}
            encryptionKey={key}
            title={ownerName}
            onShowAbout={() => setDialog('about')}
            onReport={() => setDialog('report')}
            extraMenu={[
              {
                glyph: 'translate',
                label: languageLabel(language),
                onSelect: () => setDialog('language'),
              },
            ]}
          />
        </div>

        {dialog === 'about' ? (
          <Modal title="About this conversation" onClose={() => setDialog('none')}>
            <div style={{ display: 'grid', gap: 12 }}>
              <TemporaryNotice hours={24} />
              <NotAnonymousNotice />
              <EndpointCopyingNotice />
              <EncryptionScopeNotice />
            </div>
          </Modal>
        ) : null}

        {dialog === 'language' ? (
          <LanguageDialog
            language={language}
            onClose={() => setDialog('none')}
            onChange={(next) => {
              saveVisitorLanguage(next);
              setLanguage(next);
              setDialog('none');
            }}
          />
        ) : null}

        {dialog === 'report' ? (
          <ReportDialog conversationId={id} onClose={() => setDialog('none')} />
        ) : null}
      </InterfaceThemeProvider>
    </I18nProvider>
  );
}

/** The menu label names the current language, so the state is visible without opening it. */
function languageLabel(language: LanguageCode): string {
  return supportedLanguages.find((entry) => entry.code === language)?.native ?? language;
}

/**
 * The visitor's language chooser.
 *
 * Lives inside the I18nProvider so its own strings are in the language being
 * changed away from, which is what makes it findable by someone who landed in
 * a language they cannot read.
 */
function LanguageDialog({
  language,
  onChange,
  onClose,
}: {
  language: LanguageCode;
  onChange: (language: LanguageCode) => void;
  onClose: () => void;
}) {
  const t = useT();
  return (
    <Modal title={t('settings.languageTitle')} onClose={onClose}>
      <Field label={t('settings.languageLabel')} htmlFor="visitor-language">
        {({ id }) => <LanguageSelect id={id} value={language} onChange={onChange} />}
      </Field>
      <p className="text-sm muted">{t('visitor.languageNote')}</p>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </div>
    </Modal>
  );
}
